import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Editor } from './components/Editor';
import { PreachMode } from './components/PreachMode';
import { SermonList } from './components/SermonList';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Auth } from './components/Auth';
import { ResetPassword } from './components/Auth/ResetPassword';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Research } from './components/Research';
import { Tools } from './components/Tools';
import { Settings } from './components/Settings';
import { DashboardLayout } from './components/Layout/DashboardLayout';
import { SharedSermon } from './components/SharedSermon';
import { SharedSeries } from './components/SharedSeries';
import { SharedStudyPage } from './components/SharedStudyPage';
import { SharedDevotionalPage } from './components/SharedDevotionalPage';
import { CollectionsList } from './components/Collections/CollectionsList';
import { TeamsPage } from './components/Teams/TeamsPage';
import { TeamInviteAcceptPage } from './components/Teams/TeamInviteAcceptPage';
import { AdminDashboard } from './components/Admin/AdminDashboard';
import { SeriesLibraryPage } from './components/SeriesLibrary/SeriesLibraryPage';
import { SeriesDetailPage } from './components/SeriesLibrary/SeriesDetailPage';
import { UserDashboard } from './components/Dashboard/UserDashboard';
import { AccountSetup } from './components/Auth/AccountSetup';
import { OfflineIndicator } from './components/OfflineIndicator';
import { TheologicalAssistant } from './components/TheologicalAssistant';
import { FreeStudyGuide } from './components/FreeStudyGuide';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { useAuth } from './hooks/useAuth';

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();
  const isPreachMode = location.pathname.startsWith('/dashboard/preach/') || location.pathname.startsWith('/preach/');
  const isEditorRoute = location.pathname.startsWith('/dashboard/editor');

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/auth/*" element={<Auth />} />
        <Route path="/free-study-guide" element={<FreeStudyGuide />} />
        <Route path="/shared/:id" element={<SharedSermon />} />
        <Route path="/preach/:id" element={<PreachMode isPublic />} />
        <Route path="/series/:id" element={<SharedSeries />} />
        <Route path="/study/:shareId" element={<SharedStudyPage />} />
        <Route path="/devotional/:shareId" element={<SharedDevotionalPage />} />

        {/* Protected routes */}
        <Route path="/" element={<ProtectedRoute><DashboardLayout><ErrorBoundary><UserDashboard /></ErrorBoundary></DashboardLayout></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><ErrorBoundary><UserDashboard /></ErrorBoundary></DashboardLayout></ProtectedRoute>} />
        <Route path="/dashboard/library" element={<ProtectedRoute><DashboardLayout><ErrorBoundary><SermonList /></ErrorBoundary></DashboardLayout></ProtectedRoute>} />
        <Route path="/dashboard/editor" element={<ProtectedRoute><DashboardLayout><Editor /></DashboardLayout></ProtectedRoute>} />
        <Route path="/dashboard/editor/:id" element={<ProtectedRoute><DashboardLayout><Editor /></DashboardLayout></ProtectedRoute>} />
        <Route path="/dashboard/preach/:id" element={<PreachMode />} />
        <Route path="/dashboard/research" element={<ProtectedRoute><DashboardLayout><Research /></DashboardLayout></ProtectedRoute>} />
        <Route path="/dashboard/tools" element={<ProtectedRoute><DashboardLayout><Tools /></DashboardLayout></ProtectedRoute>} />
        <Route path="/dashboard/collections" element={<ProtectedRoute><DashboardLayout><CollectionsList /></DashboardLayout></ProtectedRoute>} />
        <Route path="/dashboard/teams" element={<ProtectedRoute><DashboardLayout><TeamsPage /></DashboardLayout></ProtectedRoute>} />
        <Route path="/account-setup" element={<ProtectedRoute><AccountSetup /></ProtectedRoute>} />
        <Route path="/team-invite/:token" element={<TeamInviteAcceptPage />} />
        <Route path="/dashboard/settings" element={<ProtectedRoute><DashboardLayout><Settings /></DashboardLayout></ProtectedRoute>} />
        <Route path="/dashboard/admin" element={<ProtectedRoute><DashboardLayout><AdminDashboard /></DashboardLayout></ProtectedRoute>} />
        <Route path="/dashboard/series-library" element={<ProtectedRoute><DashboardLayout><SeriesLibraryPage /></DashboardLayout></ProtectedRoute>} />
        <Route path="/dashboard/series-library/:id" element={<ProtectedRoute><DashboardLayout><SeriesDetailPage /></DashboardLayout></ProtectedRoute>} />

        {/* Catch all route - redirect to auth for unauthenticated users */}
        <Route path="*" element={<Navigate to="/auth" replace />} />
      </Routes>
      
      {/* Theological Assistant - hidden in preach mode and editor (available via panel tab there) */}
      {user && !isPreachMode && !isEditorRoute && <TheologicalAssistant />}

      {/* Global offline / pending-sync indicator — self-hides when online and fully synced */}
      <OfflineIndicator />
    </>
  );
}

function App() {
  return (
    <DarkModeProvider>
      <Router>
        <AppContent />
      </Router>
    </DarkModeProvider>
  );
}

export default App;